import { useEffect, useRef } from 'react';
import {
  RELEVANCE_SORT,
  effectiveSortField as resolveEffectiveSortField,
  isRelevanceActive,
  resolveQueryDrivenSort,
  type WithRelevance,
} from '../services/relevanceSort';

/**
 * Wire a listing's existing sort control up to Algolia's relevance ranking.
 *
 * The listing keeps owning its sort state — the three that use this hold it in
 * three different places (component state, a persisted list-state object, a
 * parent's reducer) — so the hook takes the current value and a setter rather
 * than a `useState` of its own.
 *
 * What it adds: the auto-switch when a query is typed, the revert when the box
 * is cleared, and the memory of a manual pick that suppresses both. See
 * `services/relevanceSort.ts` for the rules and why relevance means "don't
 * re-sort" rather than "query a different index".
 *
 * `setSortField` is read through a ref, so callers can pass an inline arrow —
 * every one of them needs to reset pagination alongside the sort, and a stable
 * identity would mean a `useCallback` at each site whose only job is to satisfy
 * this hook's dependency array.
 *
 * `query` must be the term the listing is actually SHOWING results for, not the
 * raw input: the reference listing debounces into `activeQuery` and the other
 * two debounce inside their search hooks. Passing the undebounced box would
 * flip the sort to relevance a keystroke before the ranked rows exist, so the
 * list would visibly re-sort twice.
 */
export function useRelevanceSort<F extends string>(opts: {
  query: string;
  sortField: WithRelevance<F>;
  defaultSortField: F;
  setSortField: (field: WithRelevance<F>) => void;
}): {
  /** True when the rows should be left in the order the search returned them. */
  relevanceActive: boolean;
  /** Never the sentinel — what the comparator and the header highlight read. */
  effectiveSortField: F;
  /** Offer relevance as a choice? Only meaningful against a typed query. */
  relevanceAvailable: boolean;
  /** Every user-initiated sort change goes through this, including relevance. */
  pickSort: (field: WithRelevance<F>) => void;
  /**
   * Record a manual pick the caller is writing itself.
   *
   * For listings whose header click sets the field and the DIRECTION in one
   * state update — the brand listing's `updateListState` spreads a closed-over
   * object, so a second write from `pickSort` would clobber the first. Those
   * call this instead and keep their single write.
   */
  markUserPicked: () => void;
} {
  const { query, sortField, defaultSortField } = opts;

  const setSortFieldRef = useRef(opts.setSortField);
  setSortFieldRef.current = opts.setSortField;

  /**
   * Has the admin chosen a sort by hand this session?
   *
   * A ref, not state: nothing renders differently because of it, and flipping it
   * inside `pickSort` must take effect before the effect below re-runs — a state
   * update would land a render too late and the effect would immediately
   * auto-switch the pick it was told to respect.
   */
  const userPicked = useRef(false);

  useEffect(() => {
    const next = resolveQueryDrivenSort({
      query,
      sortField,
      defaultSortField,
      userPicked: userPicked.current,
    });
    if (next !== null) setSortFieldRef.current(next);
  }, [query, sortField, defaultSortField]);

  const markUserPicked = () => {
    userPicked.current = true;
  };

  const pickSort = (field: WithRelevance<F>) => {
    markUserPicked();
    setSortFieldRef.current(field);
  };

  return {
    relevanceActive: isRelevanceActive(sortField, query),
    effectiveSortField: resolveEffectiveSortField(sortField, defaultSortField),
    relevanceAvailable: query.trim() !== '',
    pickSort,
    markUserPicked,
  };
}

export { RELEVANCE_SORT };
